import { CodeDisplay, Icon, Tooltip } from '@aidkitorg/component-library';
import React, { useState } from 'react';
import { usePost } from '../../../API';
import { useLocalizedStrings } from '../../../Localization';

const LIMIT = 10; // Limit the number of values to fetch

/**
 * Component to show field examples in a tooltip
 * Fetches example values on hover and displays them in a tooltip with a question mark icon
 */
export function FieldExamplesTooltip({ fieldName }: { fieldName: string }) {
  const L = useLocalizedStrings();
  const [values, setValues] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean | null>(null); // null = not started, true = loading, false = done
  const [hasError, setHasError] = useState(false);
  const fetchFieldValuesAPI = usePost('/api/admin_panel/field_values');

  const handleOpenChange = async (open: boolean) => {
    if (open && isLoading === null) {
      setIsLoading(true);
      setHasError(false);
      try {
        const response = await fetchFieldValuesAPI({
          field: fieldName,
          limit: LIMIT
        });

        if (response && response.success && response.data?.values) {
          const truncatedValues = response.data.values.map((value: string) => 
            value.length > 30 ? `${value.slice(0, 30)}...` : value
          );
          setValues(truncatedValues);
        } else {
          setHasError(true);
        }
      } catch (error) {
        console.error('Failed to fetch field values:', error);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const tooltipContent = (
    <div className="space-y-2">
      {(isLoading === true || isLoading === null) && (
        <div className="text-xs text-gray-600">
          {L.admin_panel.settings.manage_fields_table.loading_sample_values}
        </div>
      )}
      {hasError && (
        <div className="text-xs text-gray-600">
          {L.admin_panel.settings.manage_fields_table.failed_to_load_sample_values}
        </div>
      )}
      {isLoading === false && !hasError && values.length === 0 && (
        <div className="text-xs text-gray-600">
          {L.admin_panel.settings.manage_fields_table.no_sample_values_found}
        </div>
      )}
      {isLoading === false && !hasError && values.length > 0 && (
        <div className="space-y-1">
          <div className="text-sm text-gray-700 font-medium mb-2">{L.admin_panel.settings.manage_fields_table.sampled_values}</div>
          {values.map((value, index) => (
            <CodeDisplay
              key={index}
              code={value}
              language="text"
              displayMode="block"
              copyable={false}
              showLineNumbers={false}
              variant="gray"
              className="text-xl max-w-xs"
            />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <Tooltip text={tooltipContent} onOpenChange={handleOpenChange} variant="light" asChild>
      <button
        className="inline-flex items-center justify-center w-3.5 h-3.5 text-gray-400 hover:text-gray-600 focus:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 cursor-help flex-shrink-0"
        type="button"
        aria-label={`${L.admin_panel.settings.manage_fields_table.sampled_values} ${fieldName}`}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Icon name="QuestionMarkCircleIcon" className="w-3.5 h-3.5" type="solid" />
      </button>
    </Tooltip>
  );
}
